if (!customElements.get('wlsn-nav-tabs')) {
  class NavTabs extends HTMLElement {
    constructor() {
      super();

      // Gesture state
      this.state = 'idle'; // 'idle' | 'pending' | 'dragging'
      this.startX = 0; // Start X
      this.startY = 0; // Start Y
      this.startScrollLeft = 0; // Start scroll left
      this.pointerId = null; // Pointer ID
      this.pointerType = 'mouse'; // 'mouse' | 'touch'
      this.hasDragged = false; // Is the threshold passed in this gesture

      // Momentum state
      this._raf = null;
      this._lastX = 0;
      this._lastT = 0;
      this._vx = 0;

      // Config
      this.config = {
        speed: 1, // Speed of the drag
        momentum: true, // Enable momentum
        damping: 0.0045, // Damping of the momentum
        maxMomentumMs: 1000, // Max momentum time
        thresholdMouse: 5, // Threshold for mouse
        thresholdTouch: 12, // Threshold for touch
        centerTabs: false, // Center tabs when they fit within wrapper
      };

      this.classes = { 
        inner: 'wlsn-nav-tabs__inner',
        dragging: 'is-dragging',
        centered: 'is-centered'
      };

      // Bind
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onClickCapture = this._onClickCapture.bind(this);
      this._checkAndCenter = this._checkAndCenter.bind(this);
    }

    connectedCallback() {
      this._applyAttributeConfig();
      this._setup();
    }
    disconnectedCallback() {
      this._teardown();
    }

    get elements() {
      return { inner: this.querySelector(`.${this.classes.inner}`) };
    }

    _applyAttributeConfig() {
      const num = (v, d) => (v !== null && !Number.isNaN(+v) ? +v : d);
      const bool = (v, d) => (v === null ? d : v !== 'false');
      this.config.speed = num(this.getAttribute('speed'), this.config.speed);
      this.config.damping = num(this.getAttribute('damping'), this.config.damping);
      this.config.maxMomentumMs = num(this.getAttribute('maxMomentumMs'), this.config.maxMomentumMs);
      this.config.thresholdMouse = num(this.getAttribute('threshold-mouse'), this.config.thresholdMouse);
      this.config.thresholdTouch = num(this.getAttribute('threshold-touch'), this.config.thresholdTouch);
      this.config.momentum = bool(this.getAttribute('momentum'), this.config.momentum);
      this.config.centerTabs = bool(this.getAttribute('center-tabs'), this.config.centerTabs);
    }

    _setup() {
      const { inner } = this.elements;
      if (!inner) return;

      inner.style.cursor = 'grab';
      inner.style.webkitOverflowScrolling = 'touch';
      inner.style.scrollBehavior = 'auto';
      inner.style.userSelect = 'none';
      inner.style.touchAction = 'pan-y pinch-zoom';
      inner.style.webkitUserDrag = 'none';

      // Critical: don't touch drag/click until we pass the threshold
      inner.addEventListener('pointerdown', this._onPointerDown, { passive: true });
      inner.addEventListener('click', this._onClickCapture, true);

      // Global move/up (bez capture dok ne pređemo prag)
      window.addEventListener('pointermove', this._onPointerMove, { passive: false });
      window.addEventListener('pointerup', this._onPointerUp, { passive: true });
      window.addEventListener('pointercancel', this._onPointerUp, { passive: true });
      window.addEventListener('blur', this._onPointerUp, { passive: true });

      // Disable native DnD only on visual elements (doesn't affect click)
      inner.querySelectorAll('a, button, [role="button"], [role="tab"], [data-cta], img, svg').forEach((el) => {
        el.draggable = false;
        el.style.webkitUserDrag = 'none';
      });

      // Setup centering if enabled
      if (this.config.centerTabs) {
        this._resizeObserver = new ResizeObserver(this._checkAndCenter);
        this._resizeObserver.observe(inner);
        // Also observe wrapper to catch container size changes
        const wrapper = this.querySelector('.wlsn-nav-tabs__wrapper');
        if (wrapper) {
          this._resizeObserver.observe(wrapper);
        }
        this._checkAndCenter(); // Initial check
      }
    }

    _teardown() {
      const { inner } = this.elements;
      if (inner) {
        inner.removeEventListener('pointerdown', this._onPointerDown, { passive: true });
        inner.removeEventListener('click', this._onClickCapture, true);
      }
      window.removeEventListener('pointermove', this._onPointerMove, { passive: false });
      window.removeEventListener('pointerup', this._onPointerUp, { passive: true });
      window.removeEventListener('pointercancel', this._onPointerUp, { passive: true });
      window.removeEventListener('blur', this._onPointerUp, { passive: true });
      this._stopMomentum();
      
      // Cleanup ResizeObserver
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    }

    _threshold() {
      return this.pointerType === 'mouse' ? this.config.thresholdMouse : this.config.thresholdTouch;
    }

    _onPointerDown(e) {
      const { inner } = this.elements;
      if (!inner || e.button === 2) return;
      if (e.isPrimary === false) return;

      // Phase: pending (click is still allowed)
      this.state = 'pending';
      this.hasDragged = false;
      this.pointerId = e.pointerId;
      this.pointerType = e.pointerType || 'mouse';

      this.startX = e.clientX;
      this.startY = e.clientY;
      this.startScrollLeft = inner.scrollLeft;

      // prepare for momentum (but without capture!)
      this._lastX = e.clientX;
      this._lastT = performance.now();
      this._vx = 0;

      // No preventDefault, no capture → native click remains untouched until pending
    }

    _onPointerMove(e) {
      const { inner } = this.elements;
      if (!inner || this.state === 'idle') return;

      const dxRaw = e.clientX - this.startX;
      const dyRaw = e.clientY - this.startY;
      const distance = Math.hypot(dxRaw, dyRaw);

      if (this.state === 'pending') {
        // Still no drag; don't touch click until we pass the threshold
        if (distance >= this._threshold()) {
          // Transition to dragging
          this.state = 'dragging';
          this.hasDragged = true;

          try {
            inner.setPointerCapture?.(this.pointerId);
          } catch {}
          inner.classList.add(this.classes.dragging);
          inner.style.cursor = 'grabbing';
          document.documentElement.classList.add('wlsn-no-select');
          // START MOVING IMMEDIATELY IN THE SAME FRAME
        } else {
          return; // still pending → release native behavior
        }
      }

      // We are now in dragging
      e.preventDefault(); // why: prevent text-select/scroll hijack
      const dx = dxRaw * this.config.speed;
      inner.scrollLeft = this.startScrollLeft - dx;

      // velocity
      const now = performance.now();
      const dt = Math.max(1, now - this._lastT);
      const vxInstant = (e.clientX - this._lastX) / dt;
      this._vx = this._vx * 0.8 + vxInstant * 0.2;
      this._lastX = e.clientX;
      this._lastT = now;
    }

    _onPointerUp() {
      const { inner } = this.elements;
      if (!inner) {
        this.state = 'idle';
        return;
      }

      if (this.state === 'dragging') {
        // End of drag
        inner.classList.remove(this.classes.dragging);
        inner.style.cursor = 'grab';
        try {
          inner.releasePointerCapture?.(this.pointerId);
        } catch {}
        document.documentElement.classList.remove('wlsn-no-select');

        if (this.config.momentum && Math.abs(this._vx) > 0.02) {
          this._startMomentum(inner, this._vx);
        } else {
          this._stopMomentum();
        }
        
        // Re-check centering after drag ends (in case overflow changed)
        if (this.config.centerTabs) {
          this._checkAndCenter();
        }
      } else {
        // pending → clean click, don't touch anything
        this._stopMomentum();
      }

      // Reset state
      this.state = 'idle';
      this.pointerId = null;
    }

    _onClickCapture(e) {
      // Block click only if there was drag in this gesture
      if (this.hasDragged) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.hasDragged = false; // reset after each click phase
    }

    _startMomentum(inner, vx0) {
      const start = performance.now();
      const damping = Math.max(0.001, this.config.damping);
      const capMs = Math.max(0, this.config.maxMomentumMs);

      const animate = (t) => {
        const elapsed = t - start;
        if (elapsed > capMs) return this._stopMomentum();

        const v = vx0 * Math.exp(-damping * elapsed); // px/ms
        const dt = 16;
        const dx = v * dt;

        const next = inner.scrollLeft - dx;
        const maxScroll = inner.scrollWidth - inner.clientWidth;

        if (next <= 0 || next >= maxScroll) {
          inner.scrollLeft = Math.min(Math.max(next, 0), maxScroll);
          return this._stopMomentum();
        }

        inner.scrollLeft = next;
        if (Math.abs(v) < 0.01) return this._stopMomentum();
        this._raf = requestAnimationFrame(animate);
      };

      this._raf = requestAnimationFrame(animate);
    }

    _stopMomentum() {
      if (this._raf != null) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    }

    _checkAndCenter() {
      if (!this.config.centerTabs) return;
      
      const { inner } = this.elements;
      if (!inner) return;

      // Check if content fits within container (no overflow)
      // Use a small threshold to account for rounding differences
      const hasOverflow = inner.scrollWidth > inner.clientWidth + 1;
      
      if (hasOverflow) {
        // Always use start alignment when there's overflow (drag needed)
        inner.classList.remove(this.classes.centered);
      } else {
        // Center when content fits and center-tabs is enabled
        inner.classList.add(this.classes.centered);
      }
    }
  }

  customElements.define('wlsn-nav-tabs', NavTabs);
}
